import { Link } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";
import illustration1 from "../../styles/illustrations/Demography.png";
import { DEMOGRAPHIC_IDS } from "../../types/report";
import { GetDemographicGroupsResponse } from "../../types/workplaceCulture";
import { DEMOGRAPHIC_THRESHOLD } from "../../utils/WorkplaceCulture.utils";
import { findDemographicsByIds } from "../../utils/demographics.utils";
import { ProtectedRoutes } from "../../utils/routes.utils";
import { Paper } from "../ui/Paper/Paper";
import { Typography } from "../ui/Typography/Typography";
import "./ReportDemographicComposition.scss";

interface ReportDemographicCompositionProps {
  demographicsData?: GetDemographicGroupsResponse;
  dispatchId: string;
}

export const ReportDemographicComposition = ({
  demographicsData,
  dispatchId,
}: ReportDemographicCompositionProps) => {
  const { t } = useTranslation("reportPage");

  const firstBoxDemographics = findDemographicsByIds(demographicsData, [
    DEMOGRAPHIC_IDS.FEMALE, // DEM.0043
    DEMOGRAPHIC_IDS.MALE, // DEM.0044
    DEMOGRAPHIC_IDS.NON_BINARY, // DEM.0046
    DEMOGRAPHIC_IDS.OTHER_GENDER, // DEM.0047
    DEMOGRAPHIC_IDS.NEURODIVERGENT, // DEM.0076
    DEMOGRAPHIC_IDS.NEUROTYPICAL, // DEM.0077
    DEMOGRAPHIC_IDS.NO_DISCLOSURE_NEURODIVERSITY, // DEM.0078
  ]);

  const secondBoxDemographics = findDemographicsByIds(demographicsData, [
    DEMOGRAPHIC_IDS.LGBTQ, // DEM.0051
    DEMOGRAPHIC_IDS.AGE_GROUP, // DEM.1200
    DEMOGRAPHIC_IDS.ROLE_GROUP, // DEM.1300
    DEMOGRAPHIC_IDS.NATIVE_SPEAKERS, // DEM.0070
    DEMOGRAPHIC_IDS.NON_NATIVE_SPEAKERS, // DEM.0071
    DEMOGRAPHIC_IDS.DISABILITY, // DEM.0073
  ]);

  // Render demographic list
  const renderDemographicList = (
    demographics: Array<{
      label: string;
      engagement: number;
      engagement_percent: number;
    }>
  ) => {
    return (
      <ul className="ReportDemographicComposition__list">
        {demographics.map((demographic, index) => (
          <li key={index} className="ReportDemographicComposition__list__item">
            <strong>{demographic.label}</strong> (N={demographic.engagement}){" "}
            {demographic.engagement_percent.toFixed(1)}%
          </li>
        ))}
      </ul>
    );
  };

  return (
    <Paper shadow className="ReportDemographicComposition">
      <Typography
        tagVariant="h2"
        desktop="h2"
        className="ReportDemographicComposition__title"
        color="darkerStoneBlue"
      >
        {t("demographicComposition.title")}
      </Typography>

      <div className="ReportDemographicComposition__content">
        <div className="ReportDemographicComposition__content__text">
          <Typography desktop="body2" color="secondary">
            {t("demographicComposition.warningText", {
              threshold: DEMOGRAPHIC_THRESHOLD,
            })}
            <Link to={ProtectedRoutes.WORKPLACE_CULTURE + `/${dispatchId}`}>
              {t("demographicComposition.warningLinkText")}
            </Link>
          </Typography>
        </div>

        <div className="ReportDemographicComposition__content__image">
          <img
            src={illustration1}
            alt={t("demographicComposition.illustrationAlt")}
            className="ReportDemographicComposition__illustration"
          />
        </div>
      </div>

      <div className="ReportDemographicComposition__participation">
        <Typography
          tagVariant="h3"
          desktop="h3"
          className="ReportDemographicComposition__participation__title"
          color="darkerStoneBlue"
        >
          {t("demographicComposition.participationTitle")}
        </Typography>

        {!demographicsData ||
        demographicsData.demographic_groups.length === 0 ? (
          <Typography desktop="body2" color="secondary">
            {t("demographicComposition.noDataMessage")}
          </Typography>
        ) : (
          <div className="ReportDemographicComposition__participation__boxes">
            <Paper className="ReportDemographicComposition__participation__box">
              {renderDemographicList(firstBoxDemographics)}
            </Paper>

            <Paper className="ReportDemographicComposition__participation__box">
              {renderDemographicList(secondBoxDemographics)}
            </Paper>

            <Paper className="ReportDemographicComposition__participation__box ReportDemographicComposition__participation__box--fullWidth">
              <Typography
                desktop="h3"
                className="ReportDemographicComposition__participation__box__title"
                color="darkerStoneBlue"
              >
                {t("demographicComposition.neuroDiversityInfo.title")}
              </Typography>

              <Typography desktop="body2" color="darkerStoneBlue">
                {t("demographicComposition.neuroDiversityInfo.description")}
              </Typography>
            </Paper>
          </div>
        )}
      </div>
    </Paper>
  );
};
